import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";

import useAuth from "../../utils/hooks/useAuth";

import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { ReactComponent as SmashLogo } from "../../assets/smash_logo.svg";
import DarkModeToggle from "../../components/Navigation/DarkModeToggle";

const ResetPassword = () => {
  const { token } = useParams();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverMessage, setServerMessage] = useState(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
  });

  const onSubmit = async (data) => {
    const result = await resetPassword(token, data.password);

    if (result.success) {
      alert(result.message);
      navigate("/login");
    } else {
      setServerMessage(result.message);
    }
  };

  return (
    <div className="flex flex-row items-start justify-center w-full min-h-screen bg-blue-50 dark:bg-slate-900 p-6 overflow-y-auto">
      {/* Dark Mode Toggle */}
      <div className="fixed bottom-6 right-6 z-50">
        <DarkModeToggle />
      </div>
      <div className="flex flex-col justify-start flex-grow items-center w-full h-full max-h-[620px] gap-8">
        <div className="w-full flex flex-row justify-start items-center">
          <SmashLogo className="w-20 h-auto" />
        </div>
        <div className="flex flex-col items-center justify-center flex-grow w-full">
          <div className="p-10 bg-white dark:bg-slate-800 rounded-2xl shadow-lg flex flex-col items-center gap-6 w-full max-w-md">
            <div className="flex flex-col gap-1 w-full">
              <div className="text-center text-zinc-800 dark:text-zinc-200 text-3xl font-medium font-poppins">
                Reset Password
              </div>
              <div className="text-center text-stone-500 dark:text-stone-400 text-opacity-80 text-sm">
                Please set a strong password
              </div>
              {serverMessage && (
                <div className="text-red-500 text-sm text-center py-2">
                  {serverMessage}
                </div>
              )}
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col items-center gap-4 w-full"
            >
              {/* Password */}
              <div className="flex flex-col w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold text-sm mb-1">
                  Password<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    {...register("password", {
                      required: "Password is required",
                      validate: {
                        hasMinLength: (v) =>
                          v.length >= 8 || "At least 8 characters",
                        hasUppercase: (v) =>
                          /[A-Z]/.test(v) || "Contains uppercase letter",
                        hasLowercase: (v) =>
                          /[a-z]/.test(v) || "Contains lowercase letter",
                        hasNumber: (v) => /[0-9]/.test(v) || "Contains number",
                      },
                    })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    )}
                  </button>
                </div>
                <div className="flex flex-col text-xs mt-1 ml-1 text-red-500 space-y-0.5">
                  {errors.password?.types &&
                    Object.values(errors.password.types).map((msg, idx) => (
                      <p key={idx}>• {msg}</p>
                    ))}
                </div>
              </div>

              {/* Confirm Password */}
              <div className="flex flex-col w-full">
                <label className="text-gray-900 dark:text-gray-200 font-semibold text-sm mb-1">
                  Confirm Password<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    {...register("confirmPassword", {
                      required: "Confirm Password is required",
                      validate: (value) =>
                        value === getValues("password") ||
                        "Passwords do not match",
                    })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1 ml-1">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full py-2 bg-blue-950 dark:bg-blue-700 text-white rounded-xl hover:opacity-80 mt-2"
              >
                Reset Password
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
